import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { api } from "../utils/api";
import {
  COFFEE_PASS_OFFLINE_MESSAGE,
  nextRedeemQrFetchDelayMs,
  secondsUntil,
} from "../utils/coffeePass";

const ERROR_RETRY_MS = 5000;

/**
 * Server-issued member QR that changes every 30 seconds. It never falls back to the
 * stored PNG: if the code cannot be fetched, the QR is hidden.
 */
const RotatingRedeemQr = ({ subscriptionId, size = 250 }) => {
  const [code, setCode] = useState(null); // { payload, expiresAt, ttlSeconds }
  const [errorMessage, setErrorMessage] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const fetchTimer = useRef(null);
  const mounted = useRef(true);

  const clearFetchTimer = () => {
    if (fetchTimer.current) {
      clearTimeout(fetchTimer.current);
      fetchTimer.current = null;
    }
  };

  const fetchCode = useCallback(async () => {
    clearFetchTimer();
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setCode(null);
      setErrorMessage(COFFEE_PASS_OFFLINE_MESSAGE);
      fetchTimer.current = setTimeout(fetchCode, ERROR_RETRY_MS);
      return;
    }
    try {
      const response = await api(`v1/customer/me/subscriptions/${subscriptionId}/redeem-qr`, "GET");
      if (!mounted.current) return;
      if (response?.data?.success && response.data.data?.payload) {
        const next = response.data.data;
        setCode(next);
        setErrorMessage(null);
        fetchTimer.current = setTimeout(fetchCode, nextRedeemQrFetchDelayMs(next.expiresAt));
        return;
      }
      setCode(null);
      setErrorMessage(response?.data?.message || COFFEE_PASS_OFFLINE_MESSAGE);
    } catch (error) {
      if (!mounted.current) return;
      setCode(null);
      setErrorMessage(COFFEE_PASS_OFFLINE_MESSAGE);
    }
    fetchTimer.current = setTimeout(fetchCode, ERROR_RETRY_MS);
  }, [subscriptionId]);

  useEffect(() => {
    mounted.current = true;
    fetchCode();

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        clearFetchTimer();
      } else {
        fetchCode();
      }
    };
    const onOffline = () => {
      clearFetchTimer();
      setCode(null);
      setErrorMessage(COFFEE_PASS_OFFLINE_MESSAGE);
    };
    const onOnline = () => fetchCode();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    const tick = setInterval(() => setNow(Date.now()), 1000);

    return () => {
      mounted.current = false;
      clearFetchTimer();
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [fetchCode]);

  // A code past its expiry is never shown, even if the refetch is late.
  const secondsLeft = code ? secondsUntil(code.expiresAt, now) : 0;
  const showCode = code && secondsLeft > 0;

  if (!showCode) {
    return (
      <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center" style={{ maxWidth: size + 64 }}>
        <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
          {errorMessage || "Loading your live pass..."}
        </Typography>
      </div>
    );
  }

  const ttl = code.ttlSeconds || 30;
  return (
    <Box className="flex flex-column items-center gap-2">
      <div className="border-1 shadow-2xl rounded-3xl p-4 bg-white" data-testid="rotating-redeem-qr">
        <QRCodeSVG value={code.payload} size={size} level="M" marginSize={2} />
      </div>
      <Box className="flex items-center gap-2">
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress variant="determinate" value={Math.min(100, (secondsLeft / ttl) * 100)} size={28} />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" component="div">
              {secondsLeft}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: "grey" }}>
          Live code. It refreshes automatically.
        </Typography>
      </Box>
    </Box>
  );
};

export default RotatingRedeemQr;
